import { useEffect, useMemo, useRef, useState } from 'react';
import { GetStaticProps, NextPage } from 'next';
import styled from 'styled-components';

import { SITE_URL } from '../../common/consts';
import Flat from '../../backend/s3/flat';
import Office from '../../backend/s3/office';
import Testimonial from '../../backend/testimonial';
import {
  BuyHouseOption,
  computeBuyHouseOptions,
} from '../../common/helpers/buyHouseOption.utils';
import {
  SearchOption,
  filterByText,
} from '../../common/helpers/searchOption.utils';
import useI18n from '../../common/hooks/useI18n';
import useSearchService from '../../common/hooks/useSearchService';
import { IFlat } from '../../common/model/flat.model';
import { IOffice } from '../../common/model/office.model';
import { ITestimonial } from '../../common/model/testimonial.model';

import { SearchBreadcrumb } from '../../components/search';
import {
  FlatsDisplay,
  Footer,
  Head,
  Header,
  SearchHero,
} from '../../components/shared';
import Testimonials from '../../components/shared/testimonials';

type StaticProps = {
  offices: IOffice[];
  serializedSearchOptions: string;
  buyHouseOptions: BuyHouseOption[];
  serializedFlats: string;
  testimonials: ITestimonial[];
};

type Props = StaticProps;

const Layout = styled.div`
  display: flex;
  flex: auto;
  flex-direction: column;
  box-sizing: border-box;
  min-height: 0;

  font-style: ${(props) => props.theme.font.style};
`;

const Content = styled.main`
  position: relative;
  flex: auto;
  margin-top: ${(props) => props.theme.headerHeight};

  @media ${(props) => props.theme.breakpoints.mdd} {
    margin-top: 0;
  }
`;

const HeroBottomDivider = styled.div`
  padding-top: 3rem;

  @media ${(props) => props.theme.breakpoints.smd} {
    padding-top: 2rem;
  }
`;

const BreadcrumbContainer = styled.div`
  margin-left: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  margin-right: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  margin-bottom: -1rem;
`;

const GreyBackgroundContainer = styled.div`
  background-color: ${(props) => props.theme.colors.grey};
`;

const BuyHousePage: NextPage<Props> = ({
  offices,
  serializedSearchOptions,
  buyHouseOptions,
  serializedFlats,
  testimonials,
}) => {
  const i18n = useI18n();

  const [autoCompleteValue, setAutoCompleteValue] = useState('');
  const [recommendedFlats, setRecommendedFlats] = useState([] as IFlat[]);

  const flats: IFlat[] = useMemo(() => JSON.parse(serializedFlats), [
    serializedFlats,
  ]);
  const searchOptions: SearchOption[] = useMemo(
    () => JSON.parse(serializedSearchOptions),
    [serializedSearchOptions]
  );

  const searchServiceRef = useRef(useSearchService(flats));

  useEffect(() => {
    setRecommendedFlats(searchServiceRef.current.getLastSearchsResults());
  }, []);

  return (
    <Layout>
      <Head
        title={i18n.t('dynamic.searchPage.landing.metaTitle')}
        description={i18n.t('dynamic.searchPage.landing.metaDescription')}
        canonicalUrl={`${SITE_URL}/comprar`}
      />

      <Header offices={offices} buyHouseOptions={buyHouseOptions} />

      <Content>
        <SearchHero
          autoCompleteValue={autoCompleteValue}
          autoCompleteOptions={filterByText(searchOptions, autoCompleteValue)}
          onAutoCompleteValueChange={setAutoCompleteValue}
          imageUrl={i18n.stagingT('dynamic.searchPage.landing.imageUrl')}
          i18nKeyPrefix={'dynamic.searchPage.landing.'}
        />

        <GreyBackgroundContainer>
          <HeroBottomDivider />

          <BreadcrumbContainer>
            <SearchBreadcrumb searchOptions={searchOptions} />
          </BreadcrumbContainer>

          <FlatsDisplay
            flats={recommendedFlats}
            offices={offices}
            title={i18n.t('dynamic.searchPage.landing.flatsDisplayTitle')}
            arrows={true}
          />

          <Testimonials testimonials={testimonials} />
        </GreyBackgroundContainer>
      </Content>

      <Footer />
    </Layout>
  );
};

export const getStaticProps: GetStaticProps<StaticProps> = async () => {
  const offices = await Office.getOffices();
  const [flats, searchOptions] = await Flat.getFlats();

  const buyHouseOptions = computeBuyHouseOptions(searchOptions);

  const testimonials = Testimonial.getTestimonials();

  return {
    props: {
      offices,
      serializedSearchOptions: JSON.stringify(searchOptions),
      buyHouseOptions,
      serializedFlats: Flat.serialize(flats),
      testimonials,
    },
  };
};

export default BuyHousePage;
